import { addDays, format, isValid, parseISO } from 'date-fns';

import { isValidRfc3339 } from './browserContractSemantics';
import { platformAdminContract } from './platformAdminContract';

import type {
  AppointmentCancellationInitiator,
  AppointmentKind,
  AppointmentScope,
  OneOffBookingPattern,
  PlatformAdminAppointment,
  PlatformAdminAppointmentPage,
  PlatformAdminCancellationEvidencePage,
  PlatformAdminMutationResult,
  PlatformAdminOperationStatus,
  PlatformAdminRescheduleOperationPage,
  PlatformAdminSchedulingContext,
  Recurrence,
  RescheduleCandidatePolicyHint,
  SeatType,
} from './types';

export type MomentsPlatformSelectorProvider = 'legacy_app';

export interface MomentsPlatformSelector {
  provider: MomentsPlatformSelectorProvider;
  externalId: string;
}

export interface MomentsSelectedPairSelector {
  coach: MomentsPlatformSelector;
  member: MomentsPlatformSelector;
}

export interface MomentsSelectedPairAppointmentsRequest {
  coach: MomentsPlatformSelector;
  member: MomentsPlatformSelector;
  cursor?: string | null;
  kind?: AppointmentKind;
  limit?: number;
  rangeEnd?: string;
  rangeStart?: string;
}

export interface MomentsSelectedPairAppointmentRequest extends MomentsSelectedPairSelector {
  appointmentId: string;
}

export interface MomentsSelectedPairOperationRequest extends MomentsSelectedPairSelector {
  operationId: string;
}

export interface MomentsSelectedPairOperationsRequest extends MomentsSelectedPairSelector {
  cursor?: string;
  limit: number;
}

export interface MomentsCoachWideAppointmentsRequest {
  coach: MomentsPlatformSelector;
  cursor?: string | null;
  kind?: AppointmentKind;
  limit?: number;
  rangeEnd?: string;
  rangeStart?: string;
}

export interface MomentsSelectedPairSchedulingContextRequest extends MomentsSelectedPairSelector {
  bookingWindowStart?: string;
  bookingPattern?: OneOffBookingPattern['type'];
  intent: 'book';
  kind: AppointmentKind;
  viewerTimezone: string;
  visibleWeeks: number;
}

export interface MomentsSelectedPairRescheduleSchedulingContextRequest extends MomentsSelectedPairSelector {
  appointmentId: string;
  candidateDate: string;
  candidateScope: AppointmentScope;
  kind: AppointmentKind;
  viewerTimezone: string;
  visibleWeeks: number;
}

export interface ReviewMomentsSelectedPairAppointmentRescheduleRequest extends MomentsSelectedPairSelector {
  appointmentId: string;
  expectedSeriesMutationVersion: number | null;
  expectedVersion: number;
  scope: AppointmentScope;
  seriesId: string | null;
  startAt: string;
  viewerTimezone: string;
}

export interface BookMomentsSelectedPairAppointmentRequest extends MomentsSelectedPairSelector {
  bookingPattern?: OneOffBookingPattern;
  calendarConflictOverride?: boolean;
  idempotencyKey: string;
  kind: AppointmentKind;
  recurrence: Recurrence | null;
  seatType: SeatType | null;
  startAt: string;
  viewerTimezone: string;
}

export interface RescheduleMomentsSelectedPairAppointmentRequest extends ReviewMomentsSelectedPairAppointmentRescheduleRequest {
  busyConflictsAcknowledged: boolean;
  idempotencyKey: string;
  nearTermAcknowledged: boolean;
  reviewDigest: string;
}

export interface CancelMomentsSelectedPairAppointmentRequest extends MomentsSelectedPairSelector {
  appointmentId: string;
  expectedSeriesMutationVersion: number | null;
  expectedVersion: number;
  idempotencyKey: string;
  initiator: AppointmentCancellationInitiator;
  nearTermChemistryConfirmed: boolean;
  opsNote: string;
  reason: string | null;
  scope: AppointmentScope;
}

export interface MomentsSelectedPairCancellationEvidenceRequest extends MomentsSelectedPairSelector {
  cursor?: string | null;
  limit?: number;
}

export interface MomentsSelectedPairAppointmentPage {
  appointments: PlatformAdminAppointment[];
  contractVersion: 'appointment-platform-selected-pair-page.v1';
  nextCursor: string | null;
}

export type MomentsSelectedPairAppointment = Omit<
  PlatformAdminAppointment,
  'contractVersion'
> & {
  contractVersion: 'appointment-platform-selected-pair.v1';
};

export type MomentsCoachWideAppointmentPage = PlatformAdminAppointmentPage & {
  nextCursor?: string | null;
};
export type MomentsSelectedPairCancellationEvidencePage =
  PlatformAdminCancellationEvidencePage;
export type MomentsSelectedPairMutationResult = PlatformAdminMutationResult;
export type MomentsSelectedPairOperationPage =
  PlatformAdminRescheduleOperationPage & { paginationError?: string };
export type MomentsSelectedPairOperationStatus = PlatformAdminOperationStatus;
export type MomentsSelectedPairSchedulingContext =
  PlatformAdminSchedulingContext & {
    bookingWindow?: { startDate: string; dayCount: 30 };
    selectedPair?: {
      coach: MomentsPlatformSelector & { personId: string };
      member: MomentsPlatformSelector & { personId: string };
    };
  };

export interface MomentsSelectedPairRescheduleSchedulingContext {
  candidatePolicyHints: RescheduleCandidatePolicyHint[];
  context: MomentsSelectedPairSchedulingContext;
  contractVersion: 'appointment-platform-selected-pair-reschedule-context.v2';
  truncated: false;
}

export const matchesSelectedPairContext = (
  context: MomentsSelectedPairSchedulingContext | undefined,
  selectors: MomentsSelectedPairSelector,
): boolean => {
  const pair = context?.selectedPair;
  if (!context || !pair) return false;
  return (['coach', 'member'] as const).every(
    (role) =>
      Object.is(pair[role].provider, selectors[role].provider) &&
      pair[role].externalId === selectors[role].externalId &&
      pair[role].personId === context[role].id,
  );
};

type JsonObject = Record<string, unknown>;

interface SimpleDecoder<T> {
  decode(value: unknown): T;
}

interface MomentsSelectedPairContract {
  appointment: SimpleDecoder<MomentsSelectedPairAppointment>;
  appointmentPage: SimpleDecoder<MomentsSelectedPairAppointmentPage>;
  cancellationEvidencePage: SimpleDecoder<MomentsSelectedPairCancellationEvidencePage>;
  coachWideAppointmentPage: SimpleDecoder<MomentsCoachWideAppointmentPage>;
  mutationResult: SimpleDecoder<MomentsSelectedPairMutationResult>;
  operationPage: SimpleDecoder<MomentsSelectedPairOperationPage>;
  operationStatus: SimpleDecoder<MomentsSelectedPairOperationStatus>;
  schedulingContext: SimpleDecoder<MomentsSelectedPairSchedulingContext>;
  rescheduleSchedulingContext: SimpleDecoder<MomentsSelectedPairRescheduleSchedulingContext>;
  encodeSelectedPair(selectedPair: MomentsSelectedPairSelector): {
    coach: { external_id: string; provider: MomentsPlatformSelectorProvider };
    member: { external_id: string; provider: MomentsPlatformSelectorProvider };
  };
}

const fail: (message: string) => never = (message) => {
  throw new Error(
    `Invalid Moments selected-pair Appointment response: ${message}`,
  );
};
export const validateSelectedPairRescheduleContextRequest = (
  wrapper: MomentsSelectedPairRescheduleSchedulingContext,
  request: MomentsSelectedPairRescheduleSchedulingContextRequest,
): void => {
  const { context } = wrapper;
  const target = context.rescheduleTarget;
  if (
    target === null ||
    target.id !== request.appointmentId ||
    context.kind !== request.kind ||
    context.timezone !== request.viewerTimezone ||
    context.visibleWeeks !== request.visibleWeeks ||
    !target.allowedScopes.includes(request.candidateScope) ||
    context.availability.days.length !== 1 ||
    context.availability.days[0]!.date !== request.candidateDate ||
    wrapper.candidatePolicyHints.some(
      (hint) => hint.scope !== request.candidateScope,
    )
  ) {
    fail('reschedule context does not match requested target, date or scope');
  }
};

const exactObject = (
  value: unknown,
  fields: readonly string[],
  label: string,
): JsonObject => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return fail(`${label} must be an object`);
  }
  const object = value as JsonObject;
  const actual = Object.keys(object).sort();
  const expected = [...fields].sort();
  if (
    actual.length !== expected.length ||
    actual.some((field, index) => field !== expected[index])
  ) {
    return fail(`${label} has unknown or missing fields`);
  }
  return object;
};

const stringValue = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return fail(`${label} must be a non-empty string`);
  }
  return value;
};

const nullableString = (value: unknown, label: string): string | null => {
  if (value === null) return null;
  return stringValue(value, label);
};

const encodeSelector = (selector: MomentsPlatformSelector) => ({
  external_id: selector.externalId,
  provider: selector.provider,
});

const encodeSelectedPair = (selectedPair: MomentsSelectedPairSelector) => ({
  coach: encodeSelector(selectedPair.coach),
  member: encodeSelector(selectedPair.member),
});

export const selectedPairSelectorParams = ({
  coach,
  member,
}: MomentsSelectedPairSelector): Record<string, string> => ({
  coach_external_id: coach.externalId,
  coach_provider: coach.provider,
  member_external_id: member.externalId,
  member_provider: member.provider,
});

export const legacyAppSelectedPair = (
  coachId: string,
  memberId: string,
): MomentsSelectedPairSelector => ({
  coach: { externalId: coachId, provider: 'legacy_app' },
  member: { externalId: memberId, provider: 'legacy_app' },
});

interface CoachWideQueryOptions {
  coach: MomentsPlatformSelector;
  cursor?: string | null;
  kind?: string;
  limit?: number;
  rangeEnd?: string;
  rangeStart?: string;
}

interface SelectedPairQueryOptions extends MomentsSelectedPairSelector {
  bookingWindowStart?: string;
  candidateDate?: string;
  candidateScope?: AppointmentScope;
  appointmentId?: string;
  bookingPattern?: string;
  cursor?: string | null;
  intent?: string;
  kind?: string;
  limit?: number;
  rangeEnd?: string;
  rangeStart?: string;
  viewerTimezone?: string;
  visibleWeeks?: number;
}

export const buildCoachWideQueryString = (
  values: CoachWideQueryOptions,
): string => {
  const params = new URLSearchParams({
    coach_external_id: values.coach.externalId,
    coach_provider: values.coach.provider,
  });
  const optional: Record<string, unknown> = {
    cursor: values.cursor,
    kind: values.kind,
    limit: values.limit,
    range_end: values.rangeEnd,
    range_start: values.rangeStart,
  };
  Object.entries(optional).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return params.toString();
};

export const buildSelectedPairQueryString = (
  values: SelectedPairQueryOptions,
): string => {
  const params = new URLSearchParams(selectedPairSelectorParams(values));
  const bookingContextContractVersion = values.bookingPattern
    ? 'appointment-scheduling-context.v3'
    : undefined;
  const optional: Record<string, unknown> = {
    appointment_id: values.appointmentId,
    booking_pattern: values.bookingPattern,
    booking_window_start: values.bookingWindowStart,
    candidate_date: values.candidateDate,
    candidate_scope: values.candidateScope,
    contract_version:
      values.candidateDate !== undefined
        ? 'appointment-scheduling-context-reschedule.v2'
        : bookingContextContractVersion,
    cursor: values.cursor,
    intent: values.intent,
    kind: values.kind,
    limit: values.limit,
    range_end: values.rangeEnd,
    range_start: values.rangeStart,
    viewer_timezone: values.viewerTimezone,
    visible_weeks: values.visibleWeeks,
  };
  Object.entries(optional).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  return params.toString();
};

const normalizeConflictCalendar = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const calendar = value as JsonObject;
  return {
    calendar_summary: calendar.calendar_summary,
    provider: calendar.provider,
  };
};

// MEN-5643: a calendar selected through two connections arrives once per
// connection. Dropping the connection leaves identical entries, and one calendar
// must not fail the whole response.
const normalizeConflictCalendars = (calendars: unknown[]): unknown[] => [
  ...new Map(
    calendars
      .map(normalizeConflictCalendar)
      .map((calendar) => [JSON.stringify(calendar), calendar]),
  ).values(),
];

const normalizeAvailability = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const availability = value as JsonObject;
  return {
    days: Array.isArray(availability.days)
      ? availability.days.map((dayValue) => {
          if (
            dayValue === null ||
            typeof dayValue !== 'object' ||
            Array.isArray(dayValue)
          ) {
            return dayValue;
          }
          const day = dayValue as JsonObject;
          return {
            appointment_count: day.appointment_count,
            date: day.date,
            enabled: day.enabled,
            slots: Array.isArray(day.slots)
              ? day.slots.map((slotValue) => {
                  if (
                    slotValue === null ||
                    typeof slotValue !== 'object' ||
                    Array.isArray(slotValue)
                  ) {
                    return slotValue;
                  }
                  const slot = slotValue as JsonObject;
                  const conflict = slot.calendar_conflict as JsonObject | null;
                  return {
                    calendar_conflict:
                      conflict === null
                        ? null
                        : {
                            calendars: Array.isArray(conflict.calendars)
                              ? normalizeConflictCalendars(conflict.calendars)
                              : conflict.calendars,
                            checked_occurrence_count:
                              conflict.checked_occurrence_count,
                            conflicting_occurrence_count:
                              conflict.conflicting_occurrence_count,
                          },
                    end_at: slot.end_at,
                    label: slot.label,
                    start_at: slot.start_at,
                  };
                })
              : day.slots,
          };
        })
      : availability.days,
  };
};

const normalizeMomentsSchedulingPerson = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const person = value as JsonObject;
  return {
    display_name: person.display_name,
    id: person.id ?? person.person_id,
  };
};

const normalizeMomentsSchedulingMember = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const member = value as JsonObject;
  return {
    ...(normalizeMomentsSchedulingPerson(value) as JsonObject),
    email: member.email,
  };
};

const normalizeSchedulingContext = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const object = value as JsonObject;
  if (object.contract_version !== 'appointment-scheduling-context.v1') {
    return value;
  }
  return {
    active_series: object.active_series,
    appointment_count: object.appointment_count,
    availability: normalizeAvailability(object.availability),
    ...(object.booking_pattern
      ? { booking_pattern: object.booking_pattern }
      : {}),
    cadence_weeks: object.cadence_weeks,
    coach: normalizeMomentsSchedulingPerson(object.coach),
    contract_version: 'platform-admin-scheduling-context.v1',
    duration_minutes: object.duration_minutes,
    eligibility: object.eligibility,
    kind: object.kind,
    member: normalizeMomentsSchedulingMember(object.member),
    reschedule_target: object.reschedule_target,
    seat_type: object.seat_type,
    timezone: object.timezone,
    visible_weeks: object.visible_weeks,
    warnings: object.warnings,
  };
};

const normalizeSelectedPairActionCapabilities = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const capabilities = value as JsonObject;
  return {
    cancel: capabilities.cancel,
    join: capabilities.join,
    reschedule: capabilities.reschedule,
  };
};

const normalizeSelectedPairIdentity = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const identity = value as JsonObject;
  return {
    display_name: identity.display_name,
    id: identity.id,
  };
};

const normalizeSelectedPairCancellation = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const cancellation = value as JsonObject;
  return {
    initiator: cancellation.initiator,
    near_term_chemistry_confirmed: cancellation.near_term_chemistry_confirmed,
    ops_note: cancellation.ops_note,
    reason: cancellation.reason,
    requested_at: cancellation.requested_at,
    scope: cancellation.scope,
  };
};

const normalizeAppointment = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const object = value as JsonObject;
  if (object.contract_version !== 'appointment-platform-selected-pair.v1') {
    return value;
  }
  const identities = object.identities as JsonObject | undefined;
  return {
    action_capabilities: normalizeSelectedPairActionCapabilities(
      object.action_capabilities,
    ),
    baseline_schedule: object.baseline_schedule,
    booking_status: object.booking_status,
    calendar_event_id: object.calendar_event_id ?? null,
    cancellation: normalizeSelectedPairCancellation(object.cancellation),
    coach: normalizeSelectedPairIdentity(identities?.coach),
    contract_version: 'platform-admin-appointment.v1',
    current_schedule: object.current_schedule,
    id: object.id,
    kind: object.kind,
    lifecycle_status: object.lifecycle_status,
    meeting: object.meeting,
    member: normalizeSelectedPairIdentity(identities?.member),
    seat_type: object.seat_type,
    series_id: object.series_id,
    series_mutation_version: object.series_mutation_version,
    version: object.version,
  };
};

const normalizePlatformAppointment = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const object = value as JsonObject;
  return {
    ...object,
    action_capabilities: normalizeSelectedPairActionCapabilities(
      object.action_capabilities,
    ),
    cancellation: normalizeSelectedPairCancellation(object.cancellation),
  };
};

const normalizeCoachWideAppointmentPage = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const page = value as JsonObject;
  return {
    appointments: Array.isArray(page.appointments)
      ? page.appointments.map(normalizePlatformAppointment)
      : page.appointments,
    contract_version: page.contract_version,
  };
};

const normalizeAppointmentArray = (value: unknown): unknown =>
  Array.isArray(value) ? value.map(normalizeAppointment) : value;

const decodeAppointment = (value: unknown): PlatformAdminAppointment =>
  platformAdminContract.appointment.decode(normalizeAppointment(value));

const normalizeMutationResult = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const object = value as JsonObject;
  if (
    object.contract_version !==
    'appointment-platform-selected-pair-mutation-result.v1'
  ) {
    return value;
  }
  return {
    ...object,
    appointments: normalizeAppointmentArray(object.appointments),
    contract_version: 'platform-admin-mutation-result.v1',
  };
};

const normalizeOperationStatus = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const object = value as JsonObject;
  if (
    object.contract_version !==
    'appointment-platform-selected-pair-operation.v1'
  ) {
    return value;
  }
  return {
    ...object,
    appointments: normalizeAppointmentArray(object.appointments),
    contract_version: 'platform-admin-operation.v1',
  };
};

const normalizeCancellationEvidenceAssistant = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const assistant = value as JsonObject;
  return {
    assistantId: assistant.assistant_id,
    displayName: assistant.display_name,
  };
};

const normalizeCancellationEvidenceActor = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const actor = value as JsonObject;
  return {
    // Moments omits assistant unless a scheduling assistant acted.
    ...('assistant' in actor
      ? { assistant: normalizeCancellationEvidenceAssistant(actor.assistant) }
      : {}),
    kind: actor.kind,
    personId: actor.person_id,
    role: actor.role,
  };
};

const normalizeCancellationEvidence = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const evidence = value as JsonObject;
  return {
    actor: normalizeCancellationEvidenceActor(evidence.actor),
    affectedCount: evidence.affected_count,
    affectedEndAt: evidence.affected_end_at,
    affectedStartAt: evidence.affected_start_at,
    appointmentIds: evidence.appointment_ids,
    correlationStatus: evidence.correlation_status,
    evidenceAvailable: evidence.evidence_available,
    historyId: evidence.history_id,
    initiator: evidence.initiator,
    nearTermAcknowledged: evidence.near_term_acknowledged,
    operationId: evidence.operation_id,
    opsNote: evidence.ops_note,
    proximity: evidence.proximity,
    reconciliationReason: evidence.reconciliation_reason,
    rejection: evidence.rejection,
    requestedAt: evidence.requested_at,
    scope: evidence.scope,
    source: evidence.source,
    status: evidence.status,
    terminalResult: evidence.terminal_result,
  };
};

const normalizeRescheduleOperationPage = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const page = value as JsonObject;
  if (
    page.contract_version !== 'appointment.reschedule-operation-page.admin.v2'
  ) {
    return value;
  }
  return {
    contract_version: 'platform-admin-reschedule-operation-page.v2',
    next_cursor: page.next_cursor,
    observed_at: page.observed_at,
    operations: Array.isArray(page.operations)
      ? page.operations.map((operationValue) => {
          if (
            operationValue === null ||
            typeof operationValue !== 'object' ||
            Array.isArray(operationValue)
          ) {
            return operationValue;
          }
          const operation = operationValue as JsonObject;
          const actor = operation.actor as JsonObject | null;
          return {
            actor: actor === null ? null : { role: actor.role },
            affected_count: operation.affected_count,
            affected_end_at: operation.affected_end_at,
            affected_start_at: operation.affected_start_at,
            busy_conflicts_acknowledged: operation.busy_conflicts_acknowledged,
            conflicts: operation.conflicts,
            evidence_available: operation.evidence_available,
            near_term_acknowledged: operation.near_term_acknowledged,
            operation_id: operation.operation_id,
            original_schedules: operation.original_schedules,
            phase: operation.phase,
            proposed_schedules: operation.proposed_schedules,
            reason: operation.reason,
            requested_at: operation.requested_at,
            scope: operation.scope,
            source: operation.source,
            status: operation.status,
            terminal_result: operation.terminal_result,
            timing: operation.timing,
          };
        })
      : page.operations,
  };
};

const normalizeCancellationEvidencePage = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const page = value as JsonObject;
  if (
    page.contract_version !== 'appointment-cancellation-evidence-page-admin.v1'
  ) {
    return value;
  }
  return {
    contractVersion: 'platform-admin-cancellation-evidence-page.v1',
    evidence: Array.isArray(page.evidence)
      ? page.evidence.map(normalizeCancellationEvidence)
      : page.evidence,
    nextCursor: page.next_cursor,
    observedAt: page.observed_at,
  };
};

const decodeRescheduleSchedulingContext = (
  payload: unknown,
): MomentsSelectedPairRescheduleSchedulingContext => {
  const wrapper = exactObject(
    payload,
    ['contract_version', 'context', 'truncated', 'candidate_policy_hints'],
    'reschedule context wrapper',
  );
  if (
    wrapper.contract_version !==
      'appointment-platform-selected-pair-reschedule-context.v2' ||
    wrapper.truncated !== false
  )
    return fail('reschedule context must be exact v2 and untruncated');
  const raw = exactObject(
    wrapper.context,
    [
      'active_series',
      'appointment_count',
      'availability',
      'cadence_weeks',
      'coach',
      'coach_person_id',
      'contract_version',
      'duration_minutes',
      'eligibility',
      'engagement_end_date',
      'intent',
      'kind',
      'member',
      'member_person_id',
      'reschedule_target',
      'seat_type',
      'timezone',
      'visible_weeks',
      'warnings',
      ...(wrapper.context !== null &&
      typeof wrapper.context === 'object' &&
      'booking_pattern' in wrapper.context
        ? ['booking_pattern']
        : []),
    ],
    'reschedule context',
  );
  if (
    raw.contract_version !== 'appointment-scheduling-context.v1' ||
    raw.intent !== 'reschedule'
  )
    return fail('reschedule context contract or intent is invalid');
  (['coach', 'member'] as const).forEach((role) => {
    const person = exactObject(
      raw[role],
      ['display_name', 'email', 'person_id'],
      role,
    );
    stringValue(person.email, `${role}.email`);
    if (person.person_id !== raw[`${role}_person_id`])
      fail(`${role} identity aliases must match`);
  });
  const availability = exactObject(
    raw.availability,
    ['days'],
    'reschedule availability',
  );
  if (!Array.isArray(availability.days))
    return fail('reschedule days must be an array');
  availability.days.forEach((dayValue) => {
    const day = exactObject(
      dayValue,
      ['appointment_count', 'date', 'enabled', 'slots'],
      'reschedule day',
    );
    if (!Array.isArray(day.slots)) fail('reschedule slots must be an array');
    day.slots.forEach((slotValue) => {
      const slot = exactObject(
        slotValue,
        ['calendar_conflict', 'end_at', 'label', 'start_at'],
        'reschedule slot',
      );
      if (slot.calendar_conflict !== null) {
        const conflict = exactObject(
          slot.calendar_conflict,
          [
            'calendars',
            'checked_occurrence_count',
            'conflicting_occurrence_count',
          ],
          'reschedule calendar conflict',
        );
        if (!Array.isArray(conflict.calendars))
          fail('conflict calendars must be an array');
        conflict.calendars.forEach((calendar) =>
          exactObject(
            calendar,
            [
              'calendar_summary',
              'connection_id',
              'provider',
              'provider_calendar_id',
            ],
            'conflict calendar',
          ),
        );
      }
    });
  });
  const context = platformAdminContract.schedulingContext.decode(
    normalizeSchedulingContext(raw),
  );
  if (
    context.rescheduleTarget === null ||
    context.availability.days.length !== 1
  )
    return fail('reschedule context requires a target and one candidate day');
  const hints = wrapper.candidate_policy_hints;
  if (!Array.isArray(hints) || hints.length > 104)
    return fail('reschedule hints must be an array of at most 104 candidates');
  const conflictClasses: RescheduleCandidatePolicyHint['conflictClasses'] = [
    'calendar_busy',
    'existing_mento_appointment',
    'out_of_office',
    'outside_availability',
    'minimum_notice',
    'past_or_started',
    'dependency_unavailable',
  ];
  const candidatePolicyHints = hints.map(
    (hintValue): RescheduleCandidatePolicyHint => {
      const hint = exactObject(
        hintValue,
        ['scope', 'proposed_start_at', 'selectable', 'conflict_classes'],
        'candidate hint',
      );
      if (
        (hint.scope !== 'appointment' && hint.scope !== 'this_and_following') ||
        !context.rescheduleTarget!.allowedScopes.includes(hint.scope) ||
        typeof hint.selectable !== 'boolean' ||
        typeof hint.proposed_start_at !== 'string' ||
        !isValidRfc3339(hint.proposed_start_at) ||
        !Array.isArray(hint.conflict_classes) ||
        new Set(hint.conflict_classes).size !== hint.conflict_classes.length ||
        hint.conflict_classes.some(
          (conflictClass) =>
            !conflictClasses.some((known) => known === conflictClass),
        )
      )
        return fail('candidate hint is invalid');
      if (
        hint.selectable &&
        hint.conflict_classes.some(
          (conflictClass) =>
            conflictClass === 'existing_mento_appointment' ||
            conflictClass === 'past_or_started' ||
            conflictClass === 'dependency_unavailable',
        )
      )
        return fail('blocking candidate cannot be selectable');
      return {
        conflictClasses:
          hint.conflict_classes as RescheduleCandidatePolicyHint['conflictClasses'],
        proposedStartAt: hint.proposed_start_at,
        scope: hint.scope,
        selectable: hint.selectable,
      };
    },
  );
  const day = context.availability.days[0]!;
  const { slots } = day;
  const hintStarts = new Set(
    candidatePolicyHints.map((hint) => Date.parse(hint.proposedStartAt)),
  );
  if (
    slots.length !== candidatePolicyHints.length ||
    hintStarts.size !== candidatePolicyHints.length ||
    slots.some((slot) => !hintStarts.has(Date.parse(slot.startAt)))
  )
    return fail('candidate hints must cover every slot exactly once');
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: context.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  [
    ...slots.map((slot) => slot.startAt),
    ...candidatePolicyHints.map((hint) => hint.proposedStartAt),
  ].forEach((start) => {
    const parts = Object.fromEntries(
      formatter
        .formatToParts(new Date(start))
        .map(({ type, value }) => [type, value]),
    );
    const local = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
    if (
      local.slice(0, 10) !== day.date ||
      local !== start.slice(0, 19) ||
      !['00', '15', '30', '45'].includes(parts.minute!) ||
      parts.second !== '00' ||
      /\.(?!0+(?:Z|[+-]))/u.test(start)
    )
      fail(
        'candidate start must be a viewer-offset quarter-hour on the selected day',
      );
  });
  return {
    candidatePolicyHints,
    context,
    contractVersion: 'appointment-platform-selected-pair-reschedule-context.v2',
    truncated: false,
  };
};

export const momentsSelectedPairContract: MomentsSelectedPairContract = {
  rescheduleSchedulingContext: { decode: decodeRescheduleSchedulingContext },
  appointment: {
    decode(value: unknown): MomentsSelectedPairAppointment {
      return {
        ...decodeAppointment(value),
        contractVersion: 'appointment-platform-selected-pair.v1',
      };
    },
  },
  appointmentPage: {
    decode(value: unknown): MomentsSelectedPairAppointmentPage {
      const object = exactObject(
        value,
        ['appointments', 'contract_version', 'next_cursor'],
        'appointment page',
      );
      if (
        object.contract_version !== 'appointment-platform-selected-pair-page.v1'
      ) {
        return fail('appointment page contract_version is invalid');
      }
      if (!Array.isArray(object.appointments)) {
        return fail('appointment page appointments must be an array');
      }
      return {
        appointments: object.appointments.map(decodeAppointment),
        contractVersion: 'appointment-platform-selected-pair-page.v1',
        nextCursor: nullableString(
          object.next_cursor,
          'appointment page.next_cursor',
        ),
      };
    },
  },
  cancellationEvidencePage: {
    decode(value: unknown): MomentsSelectedPairCancellationEvidencePage {
      return platformAdminContract.cancellationEvidencePage.decode(
        normalizeCancellationEvidencePage(value),
      );
    },
  },
  coachWideAppointmentPage: {
    decode(value: unknown): MomentsCoachWideAppointmentPage {
      if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return platformAdminContract.appointmentPage.decode(value);
      }
      const object = value as JsonObject;
      return {
        ...platformAdminContract.appointmentPage.decode(
          normalizeCoachWideAppointmentPage(value),
        ),
        nextCursor: nullableString(
          object.next_cursor ?? null,
          'coach-wide appointment page.next_cursor',
        ),
      };
    },
  },
  encodeSelectedPair,
  mutationResult: {
    decode(value: unknown): MomentsSelectedPairMutationResult {
      return platformAdminContract.mutationResult.decode(
        normalizeMutationResult(value),
      );
    },
  },
  operationPage: {
    decode(value: unknown): MomentsSelectedPairOperationPage {
      return platformAdminContract.rescheduleOperationPage.decode(
        normalizeRescheduleOperationPage(value),
      );
    },
  },
  operationStatus: {
    decode(value: unknown): MomentsSelectedPairOperationStatus {
      return platformAdminContract.operationStatus.decode(
        normalizeOperationStatus(value),
      );
    },
  },
  schedulingContext: {
    decode(value: unknown): MomentsSelectedPairSchedulingContext {
      if (
        value !== null &&
        typeof value === 'object' &&
        'contract_version' in value &&
        value.contract_version ===
          'appointment-platform-selected-pair-booking-context.v1'
      ) {
        const object = exactObject(
          value,
          [
            'active_series',
            'appointment_count',
            'availability',
            ...('booking_pattern' in value ? ['booking_pattern'] : []),
            'booking_window',
            'cadence_weeks',
            'coach',
            'coach_person_id',
            'contract_version',
            'duration_minutes',
            'eligibility',
            'engagement_end_date',
            'intent',
            'kind',
            'member',
            'member_person_id',
            'reschedule_target',
            'seat_type',
            'selected_pair',
            'timezone',
            'visible_weeks',
            'warnings',
          ],
          'booking context',
        );
        const window = exactObject(
          object.booking_window,
          ['start_date', 'day_count'],
          'booking window',
        );
        const start = stringValue(
          window.start_date,
          'booking window.start_date',
        );
        const parsed = parseISO(start);
        if (
          !/^\d{4}-\d{2}-\d{2}$/u.test(start) ||
          !isValid(parsed) ||
          format(parsed, 'yyyy-MM-dd') !== start
        ) {
          return fail('booking window.start_date must be a canonical date');
        }
        if (
          window.day_count !== 30 ||
          object.visible_weeks !== 5 ||
          object.kind !== 'coaching' ||
          object.intent !== 'book' ||
          object.reschedule_target !== null
        ) {
          return fail(
            'booking window requires thirty-day coaching booking context',
          );
        }
        const pair = exactObject(
          object.selected_pair,
          ['coach', 'member'],
          'selected_pair',
        );
        const decodePairPerson = (role: 'coach' | 'member') => {
          const selector = exactObject(
            pair[role],
            ['provider', 'external_id', 'person_id'],
            `selected_pair.${role}`,
          );
          if (selector.provider !== 'legacy_app')
            return fail(`selected_pair.${role}.provider must be legacy_app`);
          const externalId = stringValue(
            selector.external_id,
            `selected_pair.${role}.external_id`,
          );
          const personId = stringValue(
            selector.person_id,
            `selected_pair.${role}.person_id`,
          );
          const person = exactObject(
            object[role],
            ['display_name', 'email', 'person_id'],
            role,
          );
          stringValue(person.email, `${role}.email`);
          if (
            personId !== person.person_id ||
            personId !== object[`${role}_person_id`]
          )
            return fail(`${role} identity aliases must match`);
          return { externalId, personId, provider: 'legacy_app' as const };
        };
        const selectedPair = {
          coach: decodePairPerson('coach'),
          member: decodePairPerson('member'),
        };
        const availability = exactObject(
          object.availability,
          ['days'],
          'booking availability',
        );
        if (!Array.isArray(availability.days))
          return fail('booking availability.days must be an array');
        const conflictPolicies = availability.days.map((dayValue) => {
          const day = exactObject(
            dayValue,
            ['appointment_count', 'date', 'enabled', 'slots'],
            'booking day',
          );
          if (!Array.isArray(day.slots))
            fail('booking day.slots must be an array');
          return (day.slots as unknown[]).map((slotValue) => {
            const slot = exactObject(
              slotValue,
              ['calendar_conflict', 'end_at', 'label', 'start_at'],
              'booking slot',
            );
            if (slot.calendar_conflict === null) return null;
            const hasPolicy =
              typeof slot.calendar_conflict === 'object' &&
              ('first_occurrence_conflicting' in slot.calendar_conflict ||
                'override_required' in slot.calendar_conflict);
            const conflict = exactObject(
              slot.calendar_conflict,
              [
                'calendars',
                'checked_occurrence_count',
                'conflicting_occurrence_count',
                ...(hasPolicy
                  ? ['first_occurrence_conflicting', 'override_required']
                  : []),
              ],
              'booking conflict',
            );
            if (!Array.isArray(conflict.calendars))
              fail('booking conflict.calendars must be an array');
            (conflict.calendars as unknown[]).forEach((calendarValue) => {
              exactObject(
                calendarValue,
                [
                  'calendar_summary',
                  'connection_id',
                  'provider',
                  'provider_calendar_id',
                ],
                'booking conflict calendar',
              );
            });
            if (!hasPolicy) return {};
            if (
              typeof conflict.first_occurrence_conflicting !== 'boolean' ||
              typeof conflict.override_required !== 'boolean'
            )
              return fail('booking conflict policy fields must be booleans');
            return {
              firstOccurrenceConflicting: conflict.first_occurrence_conflicting,
              overrideRequired: conflict.override_required,
            };
          });
        });
        const normalized = normalizeSchedulingContext({
          ...object,
          contract_version: 'appointment-scheduling-context.v1',
        }) as JsonObject;
        // Window counts vary by day, and a conflict may affect just one occurrence.
        // Decode the availability with the single-occurrence floor, preserving the
        // separately validated real header and all legacy decoder rules.
        const header = platformAdminContract.schedulingContext.decode({
          ...normalized,
          availability: { days: [] },
        });
        const decoded = platformAdminContract.schedulingContext.decode({
          ...normalized,
          appointment_count: null,
          booking_pattern: { type: 'one_off' },
          cadence_weeks: null,
        });
        if (
          decoded.availability.days.length !== 30 ||
          decoded.availability.days.some(
            (day, index) =>
              day.date !== format(addDays(parsed, index), 'yyyy-MM-dd'),
          )
        ) {
          return fail('booking window requires thirty ordered contiguous days');
        }
        return {
          ...header,
          availability: {
            days: decoded.availability.days.map((day, dayIndex) => ({
              ...day,
              slots: day.slots.map((slot, slotIndex) => ({
                ...slot,
                calendarConflict: slot.calendarConflict
                  ? {
                      ...slot.calendarConflict,
                      ...conflictPolicies[dayIndex]![slotIndex]!,
                    }
                  : null,
              })),
            })),
          },
          bookingWindow: { dayCount: 30, startDate: start },
          selectedPair,
        };
      }
      return platformAdminContract.schedulingContext.decode(
        normalizeSchedulingContext(value),
      );
    },
  },
};
